import { Close, ExpandMore } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    Collapse,
    Fade,
    IconButton,
    lighten,
    styled,
    Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
    useGetAllGuestAttendeesQuery,
    useGetAllOwnerAttendeesQuery,
} from '../../../../../../app/Slices/SevenDayApiSlice';
import CustomAutocomplete from '../../../../../../layout/styledcomponents/CustomAutoComplete';
import CustomInputLabel from '../../../../../../layout/styledcomponents/CustomInputLabel';
import UserRole from '../../../../../../shared/AuthRoles';
import { capitalizeName } from '../../../../../../shared/utils';
import { ExperienceType } from '../../../../../enums/experience';
import { User } from '../../../../../types/user';
import {
    dedupeByEmail,
    getAutocompleteOptionEmail,
    isValidEmail,
    pickedEmails,
    toSelected,
} from './AttendeesSection';
import { HelpOutline } from '@mui/icons-material';
import { List, ListItem } from '@mui/material';
import CustomTooltip from '../../../../../shared/CustomTooltip';

export type StaffOption = {
    value: User;
    label: string;
    teamId: number;
    teamName: string;
};

export type FreeSoloOption = {
    inputValue: string;
    label: string;
};

export type AutocompleteStaffOption = StaffOption | FreeSoloOption;

/** MUI default label/input filter. custom `filterOptions` must chain this or typing does nothing. */
const defaultAutocompleteFilter =
    createFilterOptions<AutocompleteStaffOption>();

interface AttendeesProps {
    experienceType: ExperienceType;
    usesFaceRec: boolean;
    isEditing: boolean;
}

export const enforceMaxOnChange = (
    next: any[],
    current: Array<User | { email: string }>,
    onChange: (v: any) => void,
    clearInput: () => void,
    canAddMore: boolean,
) => {
    const nextAttendees: Array<User | { email: string }> = [];

    next.forEach((opt) => {
        if (typeof opt === 'string') {
            const email = opt.trim();
            if (isValidEmail(email)) nextAttendees.push({ email });
            return;
        }

        if (opt?.inputValue) {
            const email = String(opt.inputValue).trim();
            if (isValidEmail(email)) nextAttendees.push({ email });
            return;
        }

        if (opt?.value) nextAttendees.push(opt.value);
    });

    const deduped = dedupeByEmail(nextAttendees);

    if (!canAddMore && deduped.length > (current?.length || 0)) {
        clearInput();
        onChange(current);
        return;
    }

    onChange(deduped);
    clearInput();
};

const StaffAttendees = ({
    experienceType,
    usesFaceRec,
    isEditing,
}: AttendeesProps) => {
    const { control, watch, trigger } = useFormContext();

    const guestCount = watch('bookingInfo.guests') || 0;
    const adults = watch('bookingInfo.adults') || 0;
    const guests = watch('bookingInfo.guests') || 0;
    const staffAttending = watch('staffAttending') || [];
    const externalAttending = watch('externalAttendees') || [];
    const rsvpEnabled = Boolean(watch('rsvpConfig.rsvpEnabled'));

    const loggedInUser = UserRole();

    const { data: guestAttendees } = loggedInUser?.role.includes('guest')
        ? useGetAllGuestAttendeesQuery(loggedInUser?.company)
        : useGetAllOwnerAttendeesQuery(loggedInUser?.company);

    const maxAttendees = loggedInUser?.role.includes('owner')
        ? (guestAttendees?.length || 0) + 1 // no limit for owner attendees
        : experienceType === ExperienceType.Commercial
          ? guestCount
          : experienceType === ExperienceType.Activity
            ? guestCount
            : adults;

    const currentAttendeeCount =
        (staffAttending?.length || 0) + (externalAttending?.length || 0);
    const canAddMore = currentAttendeeCount < maxAttendees && maxAttendees > 0;

    const [staffInputValue, setStaffInputValue] = useState('');

    const [collapsedStaffGroupKeys, setCollapsedStaffGroupKeys] = useState<
        Set<string>
    >(() => new Set());

    const [
        selectAllTeamExceedsSlotsMessage,
        setSelectAllTeamExceedsSlotsMessage,
    ] = useState<string | null>(null);

    useEffect(() => {
        if (!selectAllTeamExceedsSlotsMessage) {
            return;
        }
        const timeOut = window.setTimeout(
            () => setSelectAllTeamExceedsSlotsMessage(null),
            10_000,
        );
        return () => clearTimeout(timeOut);
    }, [selectAllTeamExceedsSlotsMessage]);

    const [
        selectAllTeamExceedsSlotsGroupKey,
        setSelectAllTeamExceedsSlotsGroupKey,
    ] = useState<string | null>(null);

    useEffect(() => {
        void trigger(['staffAttending', 'externalAttendees']);
    }, [rsvpEnabled, trigger]);

    const staffOptions: StaffOption[] =
        guestAttendees?.map((user: User) => ({
            value: user,
            label: user.firstName
                ? `${user.firstName} ${user.lastName} (${user.email})`
                : user.email,
            teamId: user.department?.id,
            teamName: user.department?.name || 'Others',
        })) || [];

    const GroupHeader = styled('div')(({ theme }) => ({
        position: 'sticky',
        top: '-8px',
        padding: '4px 10px',
        color: theme.palette.primary.main,
        backgroundColor: lighten(theme.palette.primary.light, 0.85),
    }));

    const GroupItems = styled('ul')({
        padding: 0,
    });

    return (
        <Box>
            {!isEditing && (
                <CustomInputLabel
                    required={rsvpEnabled}
                    htmlFor='staff-attending__input'
                >
                    Staff Attending
                    {!canAddMore && (
                        <Typography
                            component='span'
                            sx={{
                                ml: 1,
                                fontSize: '11px',
                                fontWeight: 500,
                                color: 'error.main',
                            }}
                        >
                            Max attendees reached
                        </Typography>
                    )}
                    <CustomTooltip
                        title={
                            <Typography>
                                <List>
                                    <ListItem>
                                        Select staff members that will be
                                        attending this booking.
                                    </ListItem>

                                    <ListItem>
                                        {usesFaceRec
                                            ? 'Note: This experience uses facial recognition for entry.'
                                            : 'Note: If attendees are not selected, they won’t receive this reservation in their 7DAY account.'}
                                    </ListItem>
                                </List>
                            </Typography>
                        }
                        placement='right'
                    >
                        <HelpOutline
                            sx={{
                                ml: 0.5,
                                color: 'primary.main',
                                fontSize: '14px',
                            }}
                        />
                    </CustomTooltip>
                </CustomInputLabel>
            )}
            <Controller
                name='staffAttending'
                control={control}
                render={({
                    field: { onChange, value },
                    fieldState: { error },
                }) => {
                    const current: Array<User | { email: string }> =
                        Array.isArray(value) ? value : [];

                    const selectAllInGroup = (group: string) => {
                        const picked = new Set(pickedEmails(current));
                        const inGroupUsers = staffOptions
                            .filter(
                                (option: StaffOption) =>
                                    option.teamName === group,
                            )
                            .map((option) => option.value)
                            .filter((user: User) => !picked.has(user.email));

                        const maxStaff =
                            maxAttendees - (externalAttending?.length || 0);
                        const room = Math.max(0, maxStaff - current.length);
                        if (room === 0 || inGroupUsers.length === 0) return;
                        if (inGroupUsers.length > room) {
                            const text = `This team has ${inGroupUsers.length} members, but only ${
                                room
                            } attendee slot${
                                room === 1 ? '' : 's'
                            } available. Add people individually, or increase the number of guests.`;
                            setSelectAllTeamExceedsSlotsGroupKey(group);
                            setCollapsedStaffGroupKeys((prev) => {
                                const updated = new Set(prev);
                                updated.delete(group);
                                return updated;
                            });
                            setSelectAllTeamExceedsSlotsMessage(text);
                            return;
                        }
                        onChange(dedupeByEmail([...current, ...inGroupUsers]));
                        setStaffInputValue('');
                    };

                    return (
                        <CustomAutocomplete<any, true, false, true>
                            id='staff-attending__input'
                            multiple
                            freeSolo
                            disableCloseOnSelect={
                                maxAttendees - currentAttendeeCount !== 1
                            }
                            filterSelectedOptions
                            groupBy={(option) => option.teamName}
                            options={staffOptions}
                            value={toSelected(current)}
                            inputValue={staffInputValue}
                            onInputChange={(_, v) => setStaffInputValue(v)}
                            onClose={() => {
                                setSelectAllTeamExceedsSlotsGroupKey(null);
                                setSelectAllTeamExceedsSlotsMessage(null);
                            }}
                            renderGroup={(params) => {
                                const group = params.group;
                                const isExpanded =
                                    !collapsedStaffGroupKeys.has(group);
                                const toggleThisGroup = () => {
                                    setCollapsedStaffGroupKeys((prev) => {
                                        const updated = new Set(prev);
                                        if (updated.has(group))
                                            updated.delete(group);
                                        else updated.add(group);
                                        return updated;
                                    });
                                };

                                return (
                                    <li
                                        key={params.key}
                                        aria-expanded={isExpanded}
                                    >
                                        <GroupHeader
                                            role='group'
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                gap: 0.25,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'space-between',
                                                    gap: 0.5,
                                                    width: '100%',
                                                }}
                                            >
                                                {loggedInUser?.role.includes(
                                                    'guest',
                                                ) && (
                                                    <Box
                                                        onClick={
                                                            toggleThisGroup
                                                        }
                                                        sx={{
                                                            display: 'flex',
                                                            minWidth: 0,
                                                            alignItems:
                                                                'center',
                                                            gap: 0.25,
                                                            flex: 1,
                                                        }}
                                                    >
                                                        <IconButton
                                                            type='button'
                                                            size='small'
                                                            aria-label={
                                                                isExpanded
                                                                    ? 'Collapse group'
                                                                    : 'Expand group'
                                                            }
                                                            sx={{
                                                                p: 0.25,
                                                            }}
                                                        >
                                                            <ExpandMore
                                                                sx={{
                                                                    fontSize:
                                                                        '1.1rem',
                                                                    transform:
                                                                        isExpanded
                                                                            ? 'rotate(0deg)'
                                                                            : 'rotate(-90deg)',
                                                                    transition:
                                                                        (
                                                                            theme,
                                                                        ) =>
                                                                            theme.transitions.create(
                                                                                'transform',
                                                                                {
                                                                                    duration: 150,
                                                                                },
                                                                            ),
                                                                }}
                                                            />
                                                        </IconButton>
                                                        <Typography
                                                            component='span'
                                                            variant='caption'
                                                            fontWeight={600}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                userSelect:
                                                                    'none',
                                                            }}
                                                        >
                                                            {capitalizeName(
                                                                group,
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                <Button
                                                    type='button'
                                                    size='small'
                                                    variant='text'
                                                    sx={{
                                                        minWidth: 0,
                                                        flexShrink: 0,
                                                        fontSize: '11px',
                                                        textTransform: 'none',
                                                        ml: 'auto',
                                                    }}
                                                    disabled={!canAddMore}
                                                    onClick={() =>
                                                        selectAllInGroup(group)
                                                    }
                                                >
                                                    Select all
                                                </Button>
                                            </Box>
                                            {selectAllTeamExceedsSlotsGroupKey ===
                                                group && (
                                                <Fade
                                                    in={
                                                        selectAllTeamExceedsSlotsMessage !=
                                                        null
                                                    }
                                                    timeout={200}
                                                    unmountOnExit
                                                    onExited={() => {
                                                        setSelectAllTeamExceedsSlotsGroupKey(
                                                            null,
                                                        );
                                                        setSelectAllTeamExceedsSlotsMessage(
                                                            null,
                                                        );
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'flex-start',
                                                            gap: 0.25,
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='caption'
                                                            component='span'
                                                            sx={{
                                                                flex: 1,
                                                                minWidth: 0,
                                                                color: 'error.main',
                                                                fontSize:
                                                                    '11px',
                                                                fontWeight: 500,
                                                                lineHeight: 1.35,
                                                            }}
                                                        >
                                                            {
                                                                selectAllTeamExceedsSlotsMessage
                                                            }
                                                        </Typography>
                                                        <IconButton
                                                            type='button'
                                                            size='small'
                                                            aria-label='Dismiss warning'
                                                            onMouseDown={(
                                                                e,
                                                            ) => {
                                                                e.preventDefault();
                                                            }}
                                                            onClick={() => {
                                                                setSelectAllTeamExceedsSlotsMessage(
                                                                    null,
                                                                );
                                                            }}
                                                            sx={{
                                                                p: 0.125,
                                                                flexShrink: 0,
                                                                color: 'error.main',
                                                            }}
                                                        >
                                                            <Close
                                                                sx={{
                                                                    fontSize:
                                                                        '1rem',
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Box>
                                                </Fade>
                                            )}
                                        </GroupHeader>
                                        <Collapse
                                            in={isExpanded}
                                            timeout='auto'
                                            unmountOnExit={false}
                                            sx={{
                                                width: '100%',
                                            }}
                                        >
                                            <GroupItems>
                                                {params.children}
                                            </GroupItems>
                                        </Collapse>
                                    </li>
                                );
                            }}
                            getOptionLabel={(o: any) => {
                                if (typeof o === 'string') return o;
                                if (o?.inputValue) return o.inputValue;
                                return o?.label ?? '';
                            }}
                            isOptionEqualToValue={(o: any, v: any) => {
                                const oEmail = (
                                    o?.value?.email ||
                                    o?.inputValue ||
                                    ''
                                ).toLowerCase();
                                const vEmail = (
                                    v?.value?.email ||
                                    v?.inputValue ||
                                    ''
                                ).toLowerCase();
                                return !!oEmail && oEmail === vEmail;
                            }}
                            filterOptions={(
                                options: AutocompleteStaffOption[],
                                params: FilterOptionsState<AutocompleteStaffOption>,
                            ) => {
                                const input = params.inputValue?.trim() || '';
                                const picked = pickedEmails(current);

                                const filtered = defaultAutocompleteFilter(
                                    options,
                                    params,
                                ).filter((option: AutocompleteStaffOption) => {
                                    const email =
                                        getAutocompleteOptionEmail(option);

                                    return !picked.includes(email);
                                });

                                if (canAddMore && isValidEmail(input)) {
                                    filtered.push({
                                        inputValue: input,
                                        label: input,
                                    });
                                }

                                return filtered;
                            }}
                            onChange={(_: any, next: any[]) => {
                                enforceMaxOnChange(
                                    next,
                                    current,
                                    onChange,
                                    () => setStaffInputValue(''),
                                    canAddMore,
                                );
                            }}
                            renderTags={(tagValue: any[], getTagProps: any) =>
                                tagValue.map((opt, index) => {
                                    const email =
                                        opt?.value?.email ||
                                        opt?.inputValue ||
                                        opt?.label ||
                                        '';
                                    const first = opt?.value?.firstName;
                                    const last = opt?.value?.lastName;
                                    const label =
                                        first || last
                                            ? `${first ?? ''} ${
                                                  last ?? ''
                                              } (${email})`.trim()
                                            : email;

                                    return (
                                        <Chip
                                            {...getTagProps({
                                                index,
                                            })}
                                            key={email || index}
                                            label={label}
                                            size='small'
                                        />
                                    );
                                })
                            }
                            getOptionDisabled={() => !canAddMore}
                            label={isEditing ? undefined : 'Staff Attending'}
                            placeholder={
                                loggedInUser?.role.includes('guest') &&
                                adults + guests === 0
                                    ? 'No guests attending'
                                    : ''
                            }
                            error={!!error}
                            helperText={error && error.message}
                            fontSize='12px'
                            fontWeight={500}
                        />
                    );
                }}
            />
            {!canAddMore && maxAttendees > 0 && isEditing && (
                <Typography variant='body2' color='text.secondary'>
                    {' '}
                    Max occupancy reached
                </Typography>
            )}
        </Box>
    );
};

export default StaffAttendees;
